import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { downloadFileUsingBinString } from '@evidentid/file-utils/blobs';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface ExportEntitiesRequirements {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

interface ExportEntitiesState {
    legacyExportStatus: Record<string, OperationStatus>;
    exportStatus: Record<string, OperationStatus>;
}

// The legacy module fired this through an app.observer.onMutation() hook in
// its module-builder registration; inlined at the same transition point.
// (The tracker description was '[Requested]' for every status in the legacy
// map too — kept verbatim.)
function trackExport(app: ExportEntitiesRequirements, rpName: string, status: OperationStatus): void {
    app.tracker?.track({
        name: 'Export',
        description: `[Requested] Export for ${rpName}`,
        rpName,
    });
    const messageMap: Partial<Record<OperationStatus, string>> = {
        [OperationStatus.loading]: `[Requested] Export for ${rpName}`,
        [OperationStatus.success]: `[Finished] Export for ${rpName}`,
        [OperationStatus.error]: `[Failed] Export for ${rpName}`,
    };
    app.logger?.addBreadcrumb({
        category: 'Export',
        ...(status === OperationStatus.error && { level: Severity.error }),
        message: messageMap[status]!,
        data: { rpName },
    });
}

export const useExportEntitiesStore = defineStore('exportEntities', {
    state: (): ExportEntitiesState => ({
        legacyExportStatus: {},
        exportStatus: {},
    }),
    actions: {
        setExportStatus(payload: { rpName: string, status: OperationStatus }) {
            const { rpName, status } = payload;
            trackExport(this.$app as ExportEntitiesRequirements, rpName, status);
            this.exportStatus = {
                ...this.exportStatus,
                [rpName]: status,
            };
        },
        setLegacyExportStatus(payload: { rpName: string, status: OperationStatus }) {
            const { rpName, status } = payload;
            this.legacyExportStatus = {
                ...this.legacyExportStatus,
                [rpName]: status,
            };
        },
        async exportEntities(payload: { rpName: string }) {
            const { rpName } = payload;
            const { gatewayClient } = this.$app as ExportEntitiesRequirements;
            if (this.exportStatus[rpName] === OperationStatus.loading) {
                return;
            }
            this.setExportStatus({ rpName, status: OperationStatus.loading });
            try {
                const exportedFile = await gatewayClient.tprmUi().getTprmEntityExport(rpName);
                downloadFileUsingBinString(exportedFile, 'TPRM Export.csv');
                this.setExportStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setExportStatus({ rpName, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
    },
});
